import React, { useState } from 'react';
import {
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  FilterIcon,
  InputSearchIcon
} from '@shared/components/icons/svg-icon.tsx';
import {
  DEVICE_STATUS_FILTERS,
  STATUS_LOZENGE_TONES
} from '@modules/digital-signage/utils/digital-signage.utils.ts';
import {
  DeviceStatusFilterEnum,
  DevicesListToolbarProps
} from '@modules/digital-signage/model/group-settings.model.ts';

const DevicesListToolbar: React.FC<DevicesListToolbarProps> = ({
  query,
  onQueryChange,
  statusFilter,
  onStatusFilterChange,
  statusCounts,
  disabled
}) => {
  const { t } = useTranslation();
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const isFilterActive = statusFilter !== DeviceStatusFilterEnum.All;
  const isMenuOpen = !!filterAnchor;

  return (
    <>
      <div className="flex gap-2">
        <OutlinedInput
          fullWidth
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          disabled={disabled}
          placeholder={t('placeholder_digital_signage_group_devices_search')}
          // eslint-disable-next-line no-restricted-syntax -- input text size, an input cannot use Typography
          className="h-9.5! rounded-[10px]! [&_.MuiOutlinedInput-notchedOutline]:rounded-[10px]! [&:not(:hover):not(.Mui-focused):not(.Mui-disabled)_.MuiOutlinedInput-notchedOutline]:border-(--gray-9)! [&_.MuiInputBase-input]:py-2! [&_.MuiInputBase-input]:pr-2.5! [&_.MuiInputBase-input]:pl-0! [&_.MuiInputBase-input]:text-[13px]!"
          startAdornment={
            <InputAdornment position="start">
              <InputSearchIcon
                fill="var(--gray-2)"
                className="w-4! h-4! max-lg-xl:w-3.5! max-lg-xl:h-3.5!"
              />
            </InputAdornment>
          }
        />
        <Button
          variant="outlined"
          onClick={(event) => setFilterAnchor(event.currentTarget)}
          disabled={disabled}
          startIcon={
            <FilterIcon
              className={`w-3.5! h-3.5! ${isMenuOpen ? '[&_path]:stroke-(--primary)!' : ''}`}
            />
          }
          className={`relative shrink-0 min-w-0! h-9.5! px-3! rounded-[10px]! disabled:opacity-50! ${
            isMenuOpen
              ? 'border-(--primary)! bg-(--primary-100)! text-(--primary)!'
              : 'border-(--gray-6)! bg-(--gray-11)! hover:bg-(--gray-10)! text-(--gray-2)!'
          }`}
        >
          <Typography
            variant="body2"
            component="span"
            className="text-[13px]! text-inherit!"
          >
            {t('filters')}
          </Typography>
          {isFilterActive && (
            <span className="absolute top-0.5 end-1.5 w-2 h-2 rounded-full bg-(--primary)" />
          )}
        </Button>
      </div>
      <Menu
        anchorEl={filterAnchor}
        open={isMenuOpen}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { className: 'min-w-0! w-44 mt-1.5' } }}
      >
        {DEVICE_STATUS_FILTERS.map((filter) => (
          <MenuItem
            key={filter.value}
            onClick={() => {
              onStatusFilterChange(filter.value);
              setFilterAnchor(null);
            }}
            selected={statusFilter === filter.value}
          >
            <Typography variant="body2" className="text-[13px]!">
              {t(`digital_signage_group_status_${filter.value}`)}
            </Typography>
            <Typography
              component="span"
              variant="caption"
              className={`min-w-5 px-1.5 rounded-full text-center text-[11px]! leading-4.5! font-semibold! ${STATUS_LOZENGE_TONES[filter.tone]}`}
            >
              {statusCounts[filter.value]}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default DevicesListToolbar;
